import clsx from 'clsx';
import { useCallback } from 'react';

import TextareaField from '~components/generic/editable-field/components/TextAreaField';
import { FormElementText } from '~components/generic/editor/FormElementText';
import { FormElementTextarea } from '~components/generic/editor/FormElementTextarea';
import { FormElementVersion } from '~components/generic/editor/FormElementVersion';
import Button from '~components/generic/elements/Button';
import useAppSelector from '~hooks/useAppSelector';
import useLabels from '~hooks/useLabels';
import { IEntityComponentProps } from '~interfaces/props';
import { getRelatedEntityIds } from '~services/entity-helpers';
import { getModuleUrlIdFromData } from '~services/location-helpers';
import { selectConfigBackendUrl } from '~store/selectors';
import prefixHttps from '~utils/prefixHttps';
import { PreviewImage } from './PreviewImage';
import styles from './SketchLibrary.module.css';

export const ModuleSketchLibrary = ({ data, editing, className, onChange }: IEntityComponentProps) => {
  const {
    labelModuleAttributeSketchLibraryDescription,
    labelModuleAttributeSketchLibraryVersion,
    placeholderModuleAttributeSketchLibraryName,
    labelModuleAttributeSketchLibraryName,
    msgModuleSketchLibraryImageProcessing,
    btnModuleSketchLibraryInstallDescription,
    btnModuleSketchLibraryInstall,
    labelModuleTitleSketchLibrary,
  } = useLabels([
    'labelModuleAttributeSketchLibraryDescription',
    'labelModuleAttributeSketchLibraryVersion',
    'placeholderModuleAttributeSketchLibraryName',
    'labelModuleAttributeSketchLibraryName',
    'msgModuleSketchLibraryImageProcessing',
    'btnModuleSketchLibraryInstallDescription',
    'btnModuleSketchLibraryInstall',
    'labelModuleTitleSketchLibrary',
  ]);
  const backendUrl = useAppSelector(selectConfigBackendUrl);

  const handleChange = useCallback(
    (fieldName: string) => (value: string) => {
      onChange(fieldName, value);
    },
    [onChange],
  );
  const handleNameChange = handleChange('attributes.field_title.value');
  const handleVersionChange = handleChange('attributes.field_version');
  const handleDescriptionChange = handleChange('attributes.field_text.value');

  // Do not render if there is no data!
  if (!data) {
    return null;
  }

  // ClassName generation
  const clsModule = clsx({ ...className.module });
  const clsContainer = clsx('relative px-0', { ...className.container });

  // Get ImageId
  const imageId = getRelatedEntityIds(data, 'field_image')[0];

  // Get texts
  const fieldNameValue: string = data.attributes.field_title?.value || '';
  const fieldVersionValue: string = data.attributes.field_version || '';
  const fieldDescriptionValue: string = data.attributes.field_text?.value || '';

  // Get downloadlink
  const fileId = getRelatedEntityIds(data, 'field_assets')[0];
  const hasFile = Boolean(fileId);
  const rssUrl = hasFile
    ? `sketch://add-library?url=${encodeURIComponent(prefixHttps(`${backendUrl}/sketch-library/rss/${data.id}`))}`
    : null;

  return (
    <div className={clsModule} id={getModuleUrlIdFromData(data.id, data.type)}>
      <div className={clsContainer}>
        {editing ? (
          <div className="row c-module__header">
            <div className="col u-margin-bottom-md">
              <div className="u-margin-vertical-sm">
                <FormElementText
                  value={fieldNameValue}
                  onChange={handleNameChange}
                  label={labelModuleAttributeSketchLibraryName}
                  placeholder={placeholderModuleAttributeSketchLibraryName}
                />
              </div>
              <div className="u-margin-vertical-sm">
                <FormElementVersion
                  value={fieldVersionValue}
                  onChange={handleVersionChange}
                  label={labelModuleAttributeSketchLibraryVersion}
                />
              </div>
              <div className="u-margin-vertical-sm">
                <FormElementTextarea
                  value={fieldDescriptionValue}
                  onChange={handleDescriptionChange}
                  label={labelModuleAttributeSketchLibraryDescription}
                />
              </div>
            </div>
          </div>
        ) : (
          <article className="row flex-row-reverse">
            <div className="col col-8 d-flex flex-column">
              <header>
                <h6 className="u-margin-vertical-none">{labelModuleTitleSketchLibrary}</h6>
                <h3 className="u-margin-top-xs">{`${fieldNameValue} ${fieldVersionValue}`}</h3>
              </header>
              <section className="flex-item u-margin-bottom-md">
                <TextareaField value={fieldDescriptionValue} />{' '}
              </section>
              {Boolean(rssUrl) && (
                <footer>
                  <Button
                    className="c-button c-button--light u-padding-horizontal-md"
                    name="sketch-library-install-button"
                    label={btnModuleSketchLibraryInstall}
                    title={btnModuleSketchLibraryInstallDescription}
                    href={rssUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                    iconName="download-cloud"
                    iconCentered={false}
                    iconClassName={styles.btnIcon}
                  />
                </footer>
              )}
            </div>
            <div className="col col-4">
              <aside className="relative">
                <PreviewImage imageId={imageId} msgImageProcessing={msgModuleSketchLibraryImageProcessing} />
              </aside>
            </div>
          </article>
        )}
      </div>
    </div>
  );
};

export default ModuleSketchLibrary;
